import React, { useState, useRef, useEffect } from 'react';
import { Drawer, Button, Group, Text, Transition } from '@mantine/core';
import { IconMessageCircle, IconChevronRight, IconTrash, IconHistory } from '@tabler/icons-react';
import { useCGMPalRuntime, fetchSuggestedPrompts } from './useCGMPalRuntime';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { ChatHistorySidebar } from './ChatHistorySidebar';
import './ChatbotModal.css';

export const ChatbotModal = () => {
  const [opened, setOpened] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const runtime = useCGMPalRuntime();
  const inputRef = useRef(null);

  // Fetch suggested prompts once when component mounts
  useEffect(() => {
    const loadSuggestedPrompts = async () => {
      try {
        console.log('📋 Attempting to fetch suggested prompts...');
        const prompts = await fetchSuggestedPrompts();
        console.log('✅ Successfully fetched prompts:', prompts);
        setSuggestedPrompts(prompts);
      } catch (error) {
        console.error('❌ Failed to load suggested prompts:', error);
        // Fail silently - no suggestions will be displayed
      }
    };

    loadSuggestedPrompts();
  }, []);

  // Auto-focus input when chat finishes
  useEffect(() => {
    if (!runtime.isRunning && opened) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [runtime.isRunning, opened]);

  const handleSend = (message) => {
    runtime.append(message);
  };

  const handleSuggestionClick = (promptText) => {
    runtime.append({ content: promptText });
  };

  const handleNewChat = () => {
    if (runtime.messages.length === 0) return;
    
    if (!confirmingClear) {
      // First click - enter confirm mode
      setConfirmingClear(true);
    } else {
      // Second click - actually clear
      runtime.clearChat();
      setConfirmingClear(false);
    }
  };

  const handleSelectSession = async (sessionId) => {
    await runtime.loadHistoricalSession(sessionId);
    setShowHistory(false);
  };

  const handleSessionDeleted = (deletedSessionId) => {
    if (runtime.sessionId === deletedSessionId) {
      runtime.clearChat();
    }
  };

  const handleToggleHistory = () => {
    setShowHistory(!showHistory);
  };

  // Reset confirm state when drawer closes
  const handleDrawerClose = () => {
    setOpened(false);
    setConfirmingClear(false);
    setShowHistory(false);
  };

  return (
    <>
      {/* Floating Action Button */}
      <button
        className="chatbot-floating-button"
        onClick={() => setOpened(true)}
        aria-label="Open AI Assistant"
      >
        <IconMessageCircle size={28} />
      </button>

      {/* Main Chat Drawer */}
      <Drawer
        opened={opened}
        onClose={handleDrawerClose}
        position="right"
        size="lg"
        zIndex={10000000}
        withOverlay={false}
        withCloseButton={false}
        trapFocus={false}
        lockScroll={false}
        title={
          <Group position="apart" style={{ width: '100%', alignItems: 'center' }}>
            {/* Close button with right arrow - moved to the left */}
            <Button
              variant="subtle"
              color="gray"
              size="md"
              onClick={handleDrawerClose}
              className="chatbot-close-button"
              aria-label="Close drawer"
            >
              <IconChevronRight size={24} strokeWidth={2.5} />
            </Button>
            <Text weight={500} size="lg" style={{ flex: 1, marginLeft: '12px' }}>
              AI Health Assistant
            </Text>
            <Group spacing="xs">
              <Button
                variant="subtle"
                color={showHistory ? "grape" : "gray"}
                size="xs"
                leftIcon={<IconHistory size={16} />}
                onClick={handleToggleHistory}
                style={{
                  transition: 'all 0.2s ease',
                  fontWeight: showHistory ? 600 : 400,
                }}
              >
                {showHistory ? "Hide History" : "History"}
              </Button>
              <Button
                variant="subtle"
                color={confirmingClear ? "red" : "gray"}
                size="xs"
                leftIcon={<IconTrash size={16} />}
                onClick={handleNewChat}
                onMouseLeave={() => setConfirmingClear(false)}
                disabled={runtime.messages.length === 0}
                style={{
                  transition: 'all 0.2s ease',
                  fontWeight: confirmingClear ? 600 : 400
                }}
              >
                {confirmingClear ? "Clear Chat?" : "New Chat"}
              </Button>
            </Group>
          </Group>
        }
        styles={{
          header: {
            borderBottom: '1px solid #e9ecef',
            paddingBottom: '12px',
          },
          body: {
            height: 'calc(100% - 60px)',
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
            position: 'relative',
          },
          title: {
            width: '100%',
            marginRight: 0,
          },
        }}
      >
        <div className="chatbot-drawer-content">
          {/* History Overlay */}
          <Transition
            mounted={showHistory}
            transition="slide-right"
            duration={250}
            timingFunction="ease-out"
          >
            {(styles) => (
              <>
                {/* Backdrop */}
                <div 
                  className="chat-history-backdrop"
                  style={{ 
                    ...styles,
                    opacity: showHistory ? 0.4 : 0,
                  }}
                  onClick={() => setShowHistory(false)}
                />
                {/* History Panel */}
                <div style={styles} className="chat-history-overlay">
                  <ChatHistorySidebar
                    onSelectSession={handleSelectSession}
                    currentSessionId={runtime.sessionId}
                    onSessionDeleted={handleSessionDeleted}
                    onClose={() => setShowHistory(false)}
                  />
                </div>
              </>
            )}
          </Transition>
          
          <div className="chatbot-main-content">
            <MessageList 
              messages={runtime.messages} 
              isRunning={runtime.isRunning}
              onSuggestionClick={handleSuggestionClick}
              suggestedPrompts={suggestedPrompts}
            />
            <MessageInput 
              ref={inputRef}
              onSend={handleSend} 
              disabled={runtime.isRunning} 
            />
          </div>
        </div>
      </Drawer>
    </>
  );
};

